import React, { useState } from 'react'
import Modal from '@components/common/modal/Modal'
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  LinkText,
  MultiSelectDropDown,
} from '@components/common/form'
import FlexContainer from '@components/common/FlexContainer'
import { XSpacing, YSpacing } from '@components/common'
import { BestPracticeCategory, BestPracticeDoc, IndexHeadquarter } from '@types'

interface Props {
  doc: Partial<BestPracticeDoc> | null
  categories: BestPracticeCategory[]
  isSavingDoc: boolean
  availableHqs: IndexHeadquarter[]
  onSave: (doc: Partial<BestPracticeDoc>) => void
  onClose: () => void
}

const BestPracticeDocModal: React.FC<Props> = ({
  doc,
  categories,
  isSavingDoc,
  availableHqs,
  onSave,
  onClose,
}) => {
  const [title, setTitle] = useState(doc?.title ?? '')
  const [url, setUrl] = useState(doc?.url ?? '')
  const [categoryId, setCategoryId] = useState<string | undefined>(
    doc?.categoryId,
  )
  const [allHqs, setAllHqs] = useState(doc?.allHqs ?? false)

  const enabledIds = new Set(doc?.enabledHqs ?? [])
  const [selectedHqPrefixes, setSelectedHqPrefixes] = useState<string[]>(
    availableHqs.filter((hq) => enabledIds.has(hq.id)).map((hq) => hq.prefix),
  )

  const activeCategories = categories.filter((c) => c.active)

  const handleSave = async () => {
    if (!title.trim() || !url.trim() || !categoryId) {
      return
    }
    const enabledHqs = allHqs
      ? []
      : availableHqs
          .filter((hq) => selectedHqPrefixes.includes(String(hq.prefix)))
          .map((hq) => hq.id)
    onSave({
      ...doc,
      title: title.trim(),
      url: url.trim(),
      categoryId,
      allHqs,
      enabledHqs: enabledHqs,
    })
  }

  const modalTitle = doc?.id ? `Edit ${doc.title}` : 'New Document'

  return (
    <Modal
      title={modalTitle}
      hideModal={onClose}
      color="#001940"
      maxWidth="450px"
      width="450px"
    >
      <Input
        label="Title"
        value={title}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTitle(e.target.value)
        }
      />
      <Input
        label="URL"
        value={url}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setUrl(e.target.value)
        }
      />
      <Dropdown
        label="Category"
        value={categoryId}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setCategoryId(e.target.value)
        }
      >
        <option value="">— select —</option>
        {activeCategories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Dropdown>
      <Checkbox
        checked={allHqs}
        label="Visible to all HQs"
        marginBottom="20px"
        value="allHqs"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setAllHqs(e.target.checked)
        }
      />
      {!allHqs && (
        <MultiSelectDropDown
          label="Headquarters"
          width="100%"
          includeSelectAll
          renderCheckedOptions={true}
          options={availableHqs.map((hq) => ({
            value: String(hq.prefix),
            label: hq.name,
          }))}
          checkedOptions={selectedHqPrefixes}
          onChange={setSelectedHqPrefixes}
        />
      )}
      <YSpacing height="10px" />
      <FlexContainer justifyContent="flex-end" alignItems="center">
        <LinkText label="Cancel" onClick={onClose} color="#b30303" />
        <XSpacing width="40px" />
        <Button
          label={isSavingDoc ? 'Saving…' : 'Save Document'}
          onClick={handleSave}
        />
      </FlexContainer>
    </Modal>
  )
}

export default BestPracticeDocModal
